import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import Loading from '@jg/loading';
import Alert from '@jg/alert';
import Select from '@jg/select';
import { SingleDatePicker } from '@jg/date-picker';
import moment from 'moment';
import {
  getRequestIsInFlight,
  getRequestIsError,
} from '../../selectors/loadingSelectors';
import FormError from '../FormError/FormError';
import { REQUEST } from '../../consts';
import EVENT_TYPES from '../../consts/eventTypes';
import { IAppState } from '../../types/state.d';
import { apiThunk } from '../../actions';

type CreateEventProps = {
  isCreateEventInFlight: boolean;
  isCreateEventError: boolean;
  createConsumerEvent: typeof apiThunk.createConsumerEvent;
};
type CreateEventFormProps = {
  name: string;
  eventType: string;
  dateStart?: string;
};

type EventTypeProps = {
  setFieldValue: FormikProps<CreateEventFormProps>['setFieldValue'];
  onBlur: FormikProps<CreateEventFormProps>['handleBlur'];
  value: string;
};

const EventType = (props: EventTypeProps) => (
  <Select
    placeholder="Select Type"
    onChange={({ value }) => props.setFieldValue('eventType', value, true)}
    items={EVENT_TYPES}
  />
);

export const CreateEvent: FunctionComponent<CreateEventProps> = ({
  isCreateEventInFlight,
  isCreateEventError,
  createConsumerEvent,
}) => {
  const { t } = useTranslation();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('content.createEvent.name.required')),
    eventType: Yup.string().required(
      t('content.createEvent.eventType.required'),
    ),
  });

  const browserLocale = window.navigator.language;
  moment.locale(browserLocale);

  return (
    <Formik<CreateEventFormProps>
      initialValues={{
        eventType: '',
        name: '',
      }}
      onSubmit={({ name, eventType, dateStart }) =>
        createConsumerEvent({ name, eventType, dateStart })
      }
      validationSchema={validationSchema}
      render={formikProps => (
        <form data-qa="create-event-form" onSubmit={formikProps.handleSubmit}>
          <h2 className="jg-text--center">
            {t('content.createEvent.heading')}
          </h2>
          <div className="jg-form-group">
            <label htmlFor="eventType" className="jg-form-label">
              {t('content.createEvent.eventTypeLabel')}
            </label>
            <EventType
              setFieldValue={formikProps.setFieldValue}
              onBlur={formikProps.handleBlur}
              value={formikProps.values.eventType}
            />
            {formikProps.touched.eventType &&
              formikProps.errors.eventType && (
                <FormError message={formikProps.errors.eventType} />
              )}
          </div>
          <div className="jg-form-group">
            <label htmlFor="name" className="jg-form-label">
              {t('content.createEvent.nameLabel')}
            </label>
            <input
              type="text"
              className="jg-form-control"
              id="name"
              name="name"
              onChange={formikProps.handleChange}
              onBlur={formikProps.handleBlur}
              value={formikProps.values.name}
            />
            {formikProps.touched.name &&
              formikProps.errors.name && (
                <FormError message={formikProps.errors.name} />
              )}
          </div>
          <div className="jg-form-group">
            <label htmlFor="dateStart" className="jg-form-label">
              {t('content.createEvent.eventDateLabel')}
            </label>
            <SingleDatePicker
              id="dateStart"
              date={
                formikProps.values.dateStart &&
                moment(formikProps.values.dateStart)
              }
              numberOfMonths={1}
              displayFormat={() => moment.localeData().longDateFormat('L')}
              onChange={(momentDate: moment.Moment | null) => {
                const dateStart =
                  momentDate &&
                  momentDate.toISOString &&
                  momentDate.toISOString();
                formikProps.setFieldValue('dateStart', dateStart);
              }}
            />
          </div>
          <button
            type="submit"
            className="jg-btn jg-btn--brand jg-space-mtml"
            disabled={isCreateEventInFlight}
            data-qa="create-event-continue"
          >
            {isCreateEventInFlight ? (
              <Loading size="small" colour="purple" />
            ) : (
              t('global.button.continue')
            )}
          </button>
          {isCreateEventError && (
            <Alert
              type="warning"
              displayType="inline"
              className="jg-space-mtmd"
            >
              {t('global.error.fatal')}
            </Alert>
          )}
        </form>
      )}
    />
  );
};

const mapStateToProps = (state: IAppState) => ({
  isCreateEventInFlight: Boolean(
    getRequestIsInFlight(state, REQUEST.CREATE_EVENT),
  ),
  isCreateEventError: Boolean(getRequestIsError(state, REQUEST.CREATE_EVENT)),
});
const mapDispatchToProps = {
  createConsumerEvent: apiThunk.createConsumerEvent,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateEvent);
